export type ProductPreferences = {
  brandPreference?: "store" | "name" | "no_preference";
  organicPreference?: "prefer_organic" | "no_preference";
  foodHealthPreference?: "prefer_higher" | "no_preference";
  pricePreference?: "value" | "mid_range" | "premium" | "no_preference";
  specificBrands?: string[];
  avoidBrands?: string[];
};

export type MealPlanEvent = {
  day: string;
  occasion?: string; // Optional special occasion
  guestCount?: number; // Optional guest count
  specialRequests?: string; // Optional occasion-specific requests
};

export type MealPrepPreferences = {
  generalPreferences?: string; // Open-ended text for general meal prep preferences (e.g., "prefer oven cooking", "similar ingredients")
  // Future fields can be added here, e.g.:
  // cookDays?: string[]; // Specific days user wants to cook
  // prepDays?: string[]; // Specific days for eating prepped meals
  // batchCookingStyle?: "all_at_once" | "spread_out";
};

// Breakfast/lunch are planned as a small ROTATION of repeatable recipes
// covering the week, not one novel recipe per day like dinner.
export type RotationPreferences = {
  rotationSize?: number; // Unique recipes covering the week (backend derives a default)
  batch?: boolean; // Make-ahead batching for this slot's rotation (absent = true)
};

export type LunchPlanPreferences = RotationPreferences & {
  // "leftovers": dinners are sized up and feed the next day's lunch slot.
  // "rotation": lunch gets its own rotation, same as breakfast.
  mode: "leftovers" | "rotation";
};

// Opt-in slots beyond dinner. Dinner is always planned and is not
// represented here; absent = dinner-only plan.
export type AdditionalMealPreferences = {
  breakfast?: RotationPreferences;
  lunch?: LunchPlanPreferences;
};

export type MealPlanPreferences = {
  recipeSources: "ai_only" | "my_recipes_only" | "mixed";
  aiRecipeDays?: number; // Number of days to use AI recipes (for mixed plans)
  quickMealDays?: string[];
  customRequests?: string;
  events?: MealPlanEvent[];
  usePantryIngredients?: boolean; // Whether to incorporate pantry items in meal planning
  uniqueMeals?: number; // Number of unique meals to cook (when less than totalDays, indicates meal prepping)
  mealPrepPreferences?: MealPrepPreferences; // User's meal prep preferences
  lockedRecipeIds?: string[]; // Cookbook recipes pinned in the wizard picker; each must appear in the plan
  lockedCatalogRecipeIds?: string[]; // Catalog ("New for you") picks pinned in the wizard picker
  penciledMeals?: PlannedPenciledMeal[]; // Days claimed by the user's own meals; generation skips them
  planningMode?: PlanningMode; // Wizard mode that produced this plan (display metadata; recipeSources/aiRecipeDays remain the generation contract)
  noveltyDays?: number; // Shuffle mode's "new ideas" dial (display metadata)
  additionalMeals?: AdditionalMealPreferences; // Breakfast/lunch slots beyond the dinner baseline
  prepDays?: string[]; // Preferred batch-cooking weekdays ("Sunday"); cooks snap to these when freshness allows
};

// Wizard planning modes; persisted on FamilyProfile.planningMode after a
// successful create so the next plan pre-selects the same mode
export type PlanningMode = 'surprise_me' | 'shuffle_cookbook' | 'pick_a_few';

export type IngredientSubstitution = {
  name: string;
  quantity: number;
  unit: string;
  preparation?: string;
  optional?: boolean;
  productSearchTerms: string[];
  rationale: string;
  conversionNote?: string;
};

export type Ingredient = {
  rawText?: string;
  name: string;
  quantity: number;
  unit: string;
  preparation?: string;
  optional?: boolean;
  productSearchTerms: string[];
  substitutions?: IngredientSubstitution[];
  /**
   * Restrictions the CURRENT VIEWER holds that this optional ingredient
   * violates, e.g. ['Dairy-Free']. Set by the backend per request and never
   * persisted — the same catalog recipe conflicts for one household and not
   * another. Only ever populated on optional ingredients.
   */
  conflictsWith?: string[];
};

export type RecipeImportStatus = {
  status: "pending" | "processing" | "needs_review" | "failed" | "published";
  errorMessage?: string;
  imageUrls?: string[]; // For image-based imports
  imageHashes?: string[]; // For image deduplication
  sourceUrl?: string; // For URL-based imports
  pinterestPinUrl?: string; // Pinterest pin URL for attribution
};

export type RecipeRemixStatus = {
  status: "pending" | "processing" | "failed" | "completed";
  errorMessage?: string;
  sourceRecipeIds: string[]; // IDs of recipes being remixed
  sourceRecipeNames: string[]; // Names of recipes being remixed for display
  servings?: number; // Target servings
  customInstructions?: string; // User's custom instructions
  createdAt: string; // ISO date string
  completedAt?: string; // ISO date string
};

export type RecipeInstruction = {
  text: string;
  ingredients?: string[]; // Array of ingredient names relevant to this step
};

export type NutrientBreakdownItem = {
  ingredient: string;
  calories: number;
  protein: number;
  carbohydrates: number;
  fat: number;
  fiber: number;
  sodium: number;
  saturatedFat: number;
  transFat: number;
  sugar: number;
  cholesterol: number;
};

export type RecipeNutrition = {
  perServing: {
    calories: number;
    protein: number; // grams
    carbohydrates: number; // grams
    fat: number; // grams
    fiber: number; // grams
    sodium: number; // milligrams
    saturatedFat: number; // grams
    transFat: number; // grams
    sugar: number; // grams
    cholesterol: number; // milligrams
  };
  breakdown?: NutrientBreakdownItem[]; // Optional breakdown by ingredient
  source?: "database" | "ai_estimate" | "source_provided"; // Where the values came from
};

export type CostTier = {
  value: number;
  mid: number;
  premium: number;
};

export type RecipeCostEstimate = {
  perServing: CostTier;
  estimatedAt: string; // ISO date string
  locationId: string;
};

export type MealPlanCostEstimate = {
  total: CostTier;
  perDay: CostTier;
  perServing: CostTier;
  byRecipe: Array<{
    recipeName: string;
    servings: number;
    cost: CostTier;
  }>;
  estimatedAt: string; // ISO date string
  locationId: string;
};

export type MealPlanDineOutEstimate = {
  total: number;
  perServing: number;
  byRecipe: Array<{
    recipeName: string;
    servings: number;
    perServingUsd: number;
  }>;
};

// Admin-only, on-demand per-ingredient cost breakdown (not persisted on the
// recipe). Returned by POST /api/admin/recipes/cost-breakdown for the
// "view cost breakdown" sanity check. Mirrors the backend RecipeCostBreakdown.
export type CostBreakdownLineStatus =
  | 'priced'
  | 'missing_record'
  | 'unconvertible'
  | 'no_price'
  | 'not_an_ingredient'
  | 'skipped'
  | 'optional';

export type CostBreakdownLine = {
  name: string;
  normalizedName: string;
  quantity: number;
  unit: string;
  status: CostBreakdownLineStatus;
  cost?: CostTier;
  resolvedTiers?: Array<'value' | 'mid' | 'premium'>;
  convertedQuantity?: number;
  defaultUnit?: string;
  productTierCount?: number;
};

export type RecipeCostBreakdown = {
  recipeName: string;
  servings: number;
  locationId: string;
  coverage: number;
  matchedCount: number;
  nonOptionalCount: number;
  total: CostTier;
  perServing: CostTier;
  lines: CostBreakdownLine[];
  persisted?: {
    perServing: CostTier;
    estimatedAt: string | null;
  } | null;
};

// Provenance for recipes ingested from an external source (e.g. USDA
// MyPlate Kitchen). Absent on house catalog and user-created recipes.
export type RecipeSource = {
  id: "usda_myplate" | "nih_nhlbi";
  name: string; // Display name, e.g. "USDA MyPlate Kitchen"
  url?: string;
  license?: string;
  attribution?: string; // Upstream credit line, e.g. contributing extension program
  externalId?: string;
  category?: string; // Course as reported by the source, verbatim
};

// Star rating carried over from the external source (1-5 scale)
export type RecipeSourceRating = {
  value: number;
  count: number;
};

export type Recipe = {
  id: string;
  name: string;
  description?: string;
  prepTimeMinutes: number;
  cookTimeMinutes: number;
  totalTimeMinutes: number;
  servings: number;
  ingredients: Ingredient[];
  instructions: RecipeInstruction[];
  notes?: string;
  cuisine?: string;
  difficulty?: "easy" | "medium" | "hard";
  dietaryRestrictions?: string[];
  tags?: string[];
  formatTags?: string[];
  /** Mouthfeel, derived deterministically on the backend (crispy, saucy, ...). */
  textureTags?: string[];
  ingredientKeys?: string[]; // Canonical ingredient keys (backend-computed, e.g. "steak" → "beef")
  nutrition?: RecipeNutrition;
  costEstimate?: RecipeCostEstimate;
  estimatedDineOutPricePerServing?: {
    usd: number;
    reasoning: string;
  };
  imageUrl?: string;
  mealPrepFriendly?: boolean; // Batches and reheats well (set on catalog recipes)
  isCurated?: boolean; // True if recipe is a curated "Top pick"
  // True if other households were cooking this when the plan was generated.
  // Never persisted on the recipe — propagated onto it from the Alternative
  // for display, the same way isCurated is.
  isTrending?: boolean;
  catalogRecipeId?: string; // Links user recipe back to its catalog source
  origin: "user_recipe" | "ai_generated"; // Generated by AI or created/imported by user
  source?: RecipeSource; // External-source provenance (e.g. USDA); absent on house recipes
  sourceRating?: RecipeSourceRating;
  category?: string; // Normalized course, e.g. "main_dish" | "soup" (taxonomy only)
  // Which meal slots the planner may serve this in (a chili is a soup that
  // serves lunch and dinner). Absent on recipes predating the field.
  slots?: { breakfast?: boolean; lunch?: boolean; dinner?: boolean };
  createdAt?: string; // ISO date string
  shortId?: string; // Short ID for friendly URLs

  isSaved?: boolean;
  rating?: 'up' | 'down';
  importStatus?: RecipeImportStatus;
  remixStatus?: RecipeRemixStatus;
  usageCount?: number;
  originalServings?: number; // Original servings before scaling (only present on scaled recipes)
  // Set on naive-scaled recipes (scaled by multiplying ingredient quantities,
  // instructions untouched) whose steps mention quantities: amounts in the
  // steps still reflect originalServings, so the UI warns to follow the
  // ingredient list.
  hasUnscaledStepQuantities?: boolean;
  instacartRecipePageCache?: {
    url: string;
    expiresAt: string;
    createdAt: string;
    hash: string;
  };
};

export type CookHistory = {
  cookCount: number;
  lastCookedAt: string | null;
};

export type SubstitutionStatus = {
  status: "pending" | "generating" | "completed" | "failed"; // For async substitution
  errorMessage?: string;
};

/**
 * Live progress while an AI substitution generates, streamed onto the
 * assignment doc as the model writes the replacement. Present only during
 * the AI path — cookbook picks and re-scales never write it, so its absence
 * under an in-flight status means "no narration", not "not started".
 */
export type SubstitutionGenerationProgress = {
  /** The replacement's display name, once it has fully streamed. */
  recipeName?: string;
  /** True once the model committed to a cookbook recipe over writing one. */
  cookbookPick: boolean;
  /** Completed ingredients streamed so far. Stays zero for cookbook picks. */
  ingredientsSoFar: number;
  /** Completed instruction steps streamed so far. Zero for cookbook picks. */
  stepsSoFar: number;
  /** Volume of work done before writing starts, in characters of reasoning.
   * Advances the bar during that stretch. Never rendered as text. */
  thinkingChars: number;
  /** Whether the model has stopped reasoning and started writing. */
  outputStarted: boolean;
  /** Bumps when generation restarts (task retry or the server's corrective
   * retry), which resets the counts. */
  attempt: number;
  /**
   * When this snapshot was written. A freshness token only, never displayed
   * (see utils/progressFreshness); opaque because the shape differs by
   * channel: a Firestore Timestamp off snapshots, a serialized object off
   * the REST API.
   */
  updatedAt?: unknown;
};

export type Alternative = {
  recipe: Recipe;
  reasoning?: string; // Why this alternative was suggested
  source: 'user_recipe' | 'ai_generated' | 'assignment_reference';
  isCurated?: boolean; // True if the source catalog recipe is a curated "Top pick"
  // True if the recipe was trending across households when the plan was
  // generated. Snapshotted server-side, so an old plan shows what the user was
  // actually shown rather than tonight's list.
  isTrending?: boolean;
};

// A meal the user put on a day without a recipe: just a name, optional
// freeform ingredient lines that flow into the grocery list, and a note
export type CustomMeal = {
  name: string;
  ingredients: string[];
  note?: string;
};

// Remembered penciled-in meal (weekly repeats re-fill with one tap).
// mealType scopes the memory to a slot's chips; absent (pre-slot memories)
// reads as dinner.
export type PenciledMealMemory = CustomMeal & {
  useCount: number;
  lastUsedAt: string;
  mealType?: "breakfast" | "lunch" | "dinner";
};

// A penciled-in meal claimed at planning time: the named weekday is reserved
// and generation plans the rest of the week around it
export type PlannedPenciledMeal = CustomMeal & {
  day: string; // Weekday name, matching quickMealDays/events convention
};

// Which eating occasion an assignment covers. Absent on assignments created
// before breakfast/lunch support — treat absent as "dinner" (see
// getAssignmentMealType).
export type MealType = "breakfast" | "lunch" | "dinner";

export const getAssignmentMealType = (assignment: {
  mealType?: MealType;
}): MealType => assignment.mealType ?? "dinner";

export type MealPlanAssignment = {
  id: string; // Auto-generated document ID for subcollection
  plannedDay: string;
  plannedDate: string; // ISO date string for exact date planning
  mealType?: MealType; // Absent = dinner (plans predating breakfast/lunch support)
  // Labor date decoupled from eating date: when set (and earlier than
  // plannedDate), the batch is cooked on this date (Sunday prep for a
  // Monday-anchored cook). Absent = cooked when first eaten. Null only on
  // the wire: the reorder PATCH reads it as "clear the stored date".
  cookedOnDate?: string | null;
  servings: number;
  scaledRecipe?: Recipe; // Not present for assignment_reference (resolved via preparedOnDate)
  customMeal?: CustomMeal; // Only present when source is "custom_meal"
  reasoning: string;
  substitutionStatus?: SubstitutionStatus;
  substitutionProgress?: SubstitutionGenerationProgress; // Present only while an AI substitution generates

  // Whether the recipe was generated by the AI or selected from existing
  // recipes (existing recipes could originally be AI-generated still), or a
  // recipe-less meal the user typed in ("custom_meal")
  source: "user_recipe" | "ai_generated" | "assignment_reference" | "custom_meal";

  // Meal prep fields (optional)
  isCookDay?: boolean; // True if this is a cook day (other days reference this via preparedOnDate)
  preparedOnDate?: string; // ISO date string of when this meal was originally prepared (only for assignment_reference source)

  // RAG alternatives (optional, only populated when RAG feature is enabled)
  alternatives?: Alternative[];
  alternativesOrder?: string[]; // Fixed carousel order: [recipe-a-id, recipe-b-id, recipe-c-id]
  // Alternative.isTrending carried over when that alternative was selected,
  // so the selected recipe keeps its Trending mark after a swap
  isTrending?: boolean;
};

export type CartAdditionStatus = {
  status:
    | "pending"
    | "searching_products"
    | "matching_products"
    | "adding_to_cart"
    | "completed"
    | "failed";
  productSearchProgress: {
    current: number;
    total: number;
  };
  productMatchingProgress?: {
    current: number;
    total: number;
  };
  startedAt: string;
  completedAt?: string;
  results?: {
    success: boolean;
    message: string;
    addedItems?: Array<{
      itemName: string;
      productName: string;
      quantity: number;
      reasoning?: string;
      productPageURI?: string;
      imageUrl?: string;
      nutritionalRating?: number;
    }>;
    failedItems?: Array<{
      itemName: string;
      reason: string;
    }>;
  };
};

export type MealPlanGenerationStatus = {
  status: "pending" | "generating" | "completed" | "failed";
  errorMessage?: string; // Error message if generation failed
};

/**
 * Live progress while a plan is still being written, streamed onto the plan
 * document as each meal is decided. Present only during generation.
 *
 * These meals are a preview and can still change: they are read before the
 * plan is validated, and a recipe can be swapped afterwards. Names already
 * match how the finished plan will display them.
 */
export type MealPlanGenerationStage =
  | "parsing" // Reading the week's custom requests
  | "retrieving" // Pulling relevant cookbook recipes
  | "planning" // Deciding the week, nothing written yet
  | "writing" // Recipes arriving, one meal at a time
  | "enriching" // Nutrition, cost and photos per recipe
  | "alternatives" // Building the swap options for each day
  | "finishing" // Final checks, then save
  | "slots"; // Breakfast/lunch rotations + serving math; only on plans that have them

export type MealPlanGenerationProgress = {
  stage?: MealPlanGenerationStage; // Absent on plans started before stages existed
  meals: { plannedDay?: string; name: string }[];
  totalMeals: number;
  /** Sub-progress through the "slots" stage (per-slot builds + serving math) */
  slotsDone?: number;
  slotsTotal?: number;
  /**
   * Volume of planning work done so far, in characters of reasoning. The only
   * real signal that exists before the first recipe appears, used to advance
   * the bar during that stretch. Never rendered as text.
   */
  thinkingChars?: number;
  attempt: number; // Bumps when generation restarts, which resets meals
  /**
   * When this snapshot was written. A freshness token only, never displayed:
   * progress reaches the client through both listeners and REST refetches,
   * and this decides which copy wins (see utils/progressFreshness). Opaque
   * because the shape differs by channel: a Firestore Timestamp off
   * snapshots, a serialized object off the REST API.
   */
  updatedAt?: unknown;
};

export type GroceryListStatus = {
  status: "pending" | "generating" | "completed" | "failed"; // Async grocery list generation status
  errorMessage?: string; // Error message if generation failed
};

export type GroceryListGenerationProgress = {
  /** Completed items streamed so far. Arrives faster than is readable; the
   * UI paces its own display rather than showing every one. */
  itemsSoFar: number;
  /** Predicted final item count. An estimate from the plan's ingredient
   * count, so the bar caps short of full and completion comes from
   * groceryListStatus, never from the count reaching this. */
  estimatedTotalItems: number;
  /** Most recently completed item, for the featured line. */
  latestItem?: { name: string; quantity: number; unit: string };
  /** Volume of work done before the first item, in characters of reasoning.
   * Advances the bar during that stretch. Never rendered as text. */
  thinkingChars: number;
  /** Whether items have started streaming. */
  outputStarted: boolean;
  attempt: number; // Bumps when generation restarts, which resets the count
  /**
   * When this snapshot was written. A freshness token only, never displayed
   * (see utils/progressFreshness); opaque because the shape differs by
   * channel: a Firestore Timestamp off snapshots, a serialized object off
   * the REST API.
   */
  updatedAt?: unknown;
};

export type InstacartShoppingListCache = {
  url: string;
  expiresAt: string;
  createdAt: string;
  groceryListHash: string; // Hash of the shopping list to detect changes
};

// Family profile as it stood when the plan was generated (display-only
// history; absent on plans created before this was captured)
export type MealPlanProfileSnapshot = {
  adults?: number;
  kids?: number;
  cookingSkill?: string;
  cuisineAdventure?: number;
  dietaryRestrictions?: string[];
  allergies?: string[];
  dislikes?: string[];
  cuisinePreferences?: string[];
  avoidCuisines?: string[];
  favoriteProteins?: string[];
  avoidProteins?: string[];
  preferredCookingMethods?: string[];
  includeLeftovers?: boolean;
  flavorProfile?: string;
};

// A day removed from the plan. The server keeps the full assignment around so
// the day can be restored; this summary drives the placeholder card.
export type DeletedDaySummary = {
  plannedDate: string;
  plannedDay: string;
  mealType?: MealType; // Absent = dinner, matching assignments
  recipeName?: string; // Absent for prep-day references
  source?: MealPlanAssignment["source"]; // Custom meals skip recipe-only actions
  deletedAt?: string;
};

export type MealPlan = {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  assignments: MealPlanAssignment[];
  deletedDays?: DeletedDaySummary[]; // Only present when days have been removed
  preferences: MealPlanPreferences;
  status: "draft" | "active" | "completed";
  generationStatus: MealPlanGenerationStatus;
  generationProgress?: MealPlanGenerationProgress; // Present only while generating
  groceryList?: GroceryItem[]; // Saved when plan is activated
  groceryListStatus?: GroceryListStatus;
  groceryListProgress?: GroceryListGenerationProgress; // Present only while generating
  groceryListGeneratedAt?: string; // Timestamp when grocery list was last generated
  lastModifiedAt?: string; // Timestamp when any assignment was modified or deleted
  cartAdditionStatus?: CartAdditionStatus;
  instacartShoppingListCache?: InstacartShoppingListCache;
  summary?: string; // AI-generated high-level summary of the meal plan with commentary on preferences
  isShared?: boolean; // Whether this meal plan is publicly shareable
  shortId?: string; // Short ID for friendly URLs
  aiSubstitutionsUsed: number;
  maxAiSubstitutions: number;
  additionalItems?: string[]; // Freeform text inputs for additional grocery/household items
  productMatchingInstructions?: string; // General instructions for AI when selecting products for all items
  productPreferenceOverrides?: ProductPreferences;
  calendarSyncedAt?: string;
  costEstimate?: MealPlanCostEstimate;
  dineOutEstimate?: MealPlanDineOutEstimate;
  profileSnapshot?: MealPlanProfileSnapshot;
};

export type GroceryItem = {
  itemName: string;
  totalQuantity: number;
  unit: string;
  productSearchTerms: string[];
  usedInRecipes: string[];
  optional: boolean;
  category?: string; // AI-generated category for grocery shopping organization
  alreadyHave?: boolean; // User-indicated items they already have
  pantryQuantity?: number; // Quantity available in pantry (for auto-removal from shopping list)
  pantryUnit?: string; // Unit for pantry quantity (may differ from shopping list unit)
  productPreference?: string; // User's specific preference for product selection (e.g., "spicy salsa", "organic only", "brand name preferred")
  productPreferenceScope?: "global" | "ingredient" | "category";
  isAdditionalItem?: boolean; // True if this item was added as an additional item (not from recipes)
};
